// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tabs from "@mui/material/Tabs";
import { styled, type Theme } from "@mui/material/styles";
import { type MRT_RowData, type MRT_TableInstance } from "material-react-table";
import { type ComponentProps } from "react";
import LoadingButton from "_shared/_views/loading/loading-button";
import {
  TABLE_ADD_ACTIONS_ATTRIBUTE,
  TABLE_ADD_ACTIONS_WITH_STANDALONE_ATTRIBUTE,
} from "_shared/_views/tables/table-add-actions";
// #endregion

/** Page actions share MUI's medium button size across list pages and tabs. */
const tableActionStyles = {
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon > .MuiSvgIcon-root, & .MuiButton-endIcon > .MuiSvgIcon-root": {
    fontSize: 20,
  },
} as const;
export const TableActionButton = styled(Button)(tableActionStyles) as typeof Button;
export const TableActionLoadingButton = styled(LoadingButton)(tableActionStyles);
/** The compact counterpart in the title row on narrow screens. */
export const TableActionIconButton = styled(IconButton)({ padding: 0 }) as typeof IconButton;

/**
 * The narrow-screen toolbar row: the search box at one end, the table's buttons at the other, set
 * down from whatever sits above it rather than flush against it.
 *
 * `space-between` alone leaves the two ends touching once a page runs out of slack — a page that
 * also draws a create button in the toolbar shares this width with it — so the gap is the floor
 * under that, and `space-between` still opens them wider where there is room to spare.
 */
export const MobileToolbarButtonsContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
  minWidth: 0,
  width: "100%",
  ...phoneSearchFillsTheRow(theme),
}));

/**
 * On a phone, the search in a narrow toolbar row takes all the width its neighbors leave, for the row's
 * `styled` rules. The row is `[search ——— ] [sort] [filter] [⋯]` on a card list (#7224).
 *
 * At its natural width the field stopped at 235px of a 393px phone and left a gap before the icons. The
 * flex item is MRT's horizontal `Collapse` around the field, not the field, so a `flex` on the field
 * does nothing: the `Collapse` grows, and its two wrappers and the field fill it.
 */
export function phoneSearchFillsTheRow(theme: Theme) {
  return {
    [theme.breakpoints.down("sm")]: {
      "& > .MuiCollapse-root": { flex: "1 1 0", minWidth: 0 },
      "& > .MuiCollapse-root .MuiCollapse-wrapper, & > .MuiCollapse-root .MuiCollapse-wrapperInner": {
        width: "100%",
      },
      "& > .MuiCollapse-root .MuiFormControl-root": { marginRight: 0, width: "100%" },
    },
  };
}

/**
 * The title row, holding the page name and — on a narrow screen — the page's create action.
 *
 * The create button belongs here rather than in `renderToolbarInternalActions`, which MRT lays out
 * as its own grid cell: a cell there takes its width from the toolbar for the toolbar's whole
 * height, so the search row underneath ends short of the right edge by exactly the button's width.
 * Worse, a button that waits on a permissions query appears late, and the row visibly shortens a
 * second after the page settles.
 */
export const TableTitleRow = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  minWidth: 0,
  width: "100%",
}));

/**
 * A page tab strip that takes the width it is given instead of demanding the width of its labels.
 *
 * Pair with `variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile`. The scroller
 * carries the content width of its own accord, so it needs a zero floor of its own — the container
 * giving one up is not enough.
 */
export const ScrollableTabs = styled(Tabs)(() => ({
  maxWidth: "100%",
  minWidth: 0,
  "& .MuiTabs-scroller": {
    minWidth: 0,
  },
}));

/**
 * The wide-screen row: the search box, then the table's buttons, on one line.
 *
 * The floor on the search matters. MRT wraps the field in a horizontal `Collapse`, and that wrapper
 * is the flex item here — it carries `flex: 0 1 auto`, so on any page that also draws a create
 * button in this row it is the thing that gives way, and the field had been shrinking to 71px of
 * the 280 it asks for. Nothing about that is visible: the placeholder just stops mid-word.
 */
export const ToolbarButtons = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  // The group itself does not give way either. A page that draws a create action beside these
  // shares the row with it, and the buttons are already only as wide as their icons.
  flexShrink: 0,
  flexWrap: "nowrap",
  "& > .MuiCollapse-root": {
    flexShrink: 0,
  },
}));

/**
 * A collection's whole toolbar on one wrapping row: its search box, then its add control, then the
 * view buttons at the far end.
 *
 * The order is what #4955's review asked for — the add control to the RIGHT of the search where there
 * is room for it, and underneath where there is not. Nothing here names a breakpoint, because the
 * question is not how wide the WINDOW is: these collections are drawn in a tab inside a pane inside
 * the window, beside a templates panel that opens and closes, and each of those takes a share before
 * the toolbar sees what is left. Wrapping is the browser answering that question from the real width
 * every time it changes, which is the only measurement that stays right through all of it.
 *
 * The search keeps its natural width and the add control never gives way, so the row breaks rather
 * than squeezing either into something unreadable — which is what the toolbars did before, silently,
 * with MRT clipping whatever ran past the edge and no scrollbar to say so.
 */
/**
 * The width under which the row cannot hold all three pieces, so one of them has to give way.
 *
 * The search asks for 280px, the three view buttons come to 120, and the add control's label is the
 * variable — "Upload Client Policy Document" is 272px of it. 44rem holds the longest of those
 * together with the rest; under that something wraps.
 */
const TOOLBAR_HOLDS_THE_WHOLE_ROW = "44rem";
/**
 * The same width for an add control with a standalone action beside it. The widest is the fee
 * schedule's: "Add New Category" and its dropdown, then "Save as Template", 396px together (#5808).
 * With the search and the view buttons that row measures 767px, and 50rem holds it.
 */
const TOOLBAR_HOLDS_THE_WHOLE_ROW_WITH_STANDALONE = "50rem";
const TOOLBAR_CONTAINER = "collection-toolbar";
/** How `CollectionToolbar` addresses the search's place without reaching for an Emotion class name. */
const COLLECTION_TOOLBAR_SEARCH_ATTRIBUTE = "data-collection-toolbar-search";

/**
 * A phone's toolbar, measured: 345–377px across the proposal tabs at a 393px viewport (#5907). Under
 * this the row stops spending width on comfort it cannot afford — see the two rules it gates below.
 */
const TOOLBAR_IS_PHONE_SIZED = "26rem";
/** The narrowest the search gives way to on a phone, which still shows "Search" and a few letters typed. */
const PHONE_SEARCH_MINIMUM_WIDTH = 140;

export const CollectionToolbar = styled("div")(({ theme }) => ({
  alignItems: "center",
  /**
   * The toolbar's own width is what is being asked about, so the toolbar is the container.
   *
   * Not the window, for the reason given below, and not the proposal pane either: these collections
   * are drawn on the contracts page as well, where there is no pane to measure. Containing the inline
   * size is safe here because this element's width comes from its parent, never from its contents.
   */
  containerName: TOOLBAR_CONTAINER,
  containerType: "inline-size",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  minWidth: 0,
  width: "100%",
  /**
   * What gives way is the add control, not the view buttons.
   *
   * Wrapping alone put the add control on the second line and pushed the view buttons down to a
   * third, which left the Policies tab's two collections — Client above Vendor, and only the viewer's
   * own side takes an upload — drawing their search boxes in visibly different places. The search and
   * the view buttons are the pair that reads as one strip, so they stay together and the add control
   * takes the line below.
   *
   * An `order` rather than a `flex-basis: 100%`, because flex assigns lines in order-modified
   * document order: a full-width basis would give the add control its own line at every width,
   * including the wide one where #4955's review asked for it beside the search.
   */
  [`@container ${TOOLBAR_CONTAINER} (width < ${TOOLBAR_HOLDS_THE_WHOLE_ROW})`]: {
    [`& > [${TABLE_ADD_ACTIONS_ATTRIBUTE}]`]: {
      order: 1,
    },
  },
  /**
   * The wider group takes a line of its own below the search, not only a later place in the order. An
   * `order` alone leaves it on the first line wherever it still fits there, which draws it AFTER the
   * view buttons. The full-width basis applies only under this width, so the wide toolbar keeps it
   * beside the search.
   */
  [`@container ${TOOLBAR_CONTAINER} (width < ${TOOLBAR_HOLDS_THE_WHOLE_ROW_WITH_STANDALONE})`]: {
    [`& > [${TABLE_ADD_ACTIONS_WITH_STANDALONE_ATTRIBUTE}]`]: {
      flexBasis: "100%",
      order: 1,
    },
  },
  [`@container ${TOOLBAR_CONTAINER} (width < ${TOOLBAR_IS_PHONE_SIZED})`]: {
    /**
     * The search gives way to the view buttons rather than the buttons wrapping under it. At its own
     * 201px the pair needed 329px, which a phone's toolbar holds by 16px at most — and a page scrollbar
     * or the prose inset takes that, leaving the three buttons alone on a line of their own.
     */
    [`& > [${COLLECTION_TOOLBAR_SEARCH_ATTRIBUTE}]`]: {
      flex: "1 1 0",
      minWidth: PHONE_SEARCH_MINIMUM_WIDTH,
      "& > .MuiCollapse-root, & .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
        flexShrink: 1,
        minWidth: 0,
        width: "100%",
      },
      "& .MuiFormControl-root": { width: "100%" },
    },
    /**
     * Save as Template beside the add control, not under it. "Add New Category" with its dropdown and
     * "Save as Template" came to 395px against a 377px toolbar; tighter padding, a 4px icon gap and a
     * narrower dropdown bring them to 365px, so the pair shares a line wherever a phone gives it one.
     * At 6px of padding they came to 377px, and a 390px phone's answer draft has a 374px toolbar, so
     * Save as Template wrapped under a line with room for it (#6242).
     */
    [`& [${TABLE_ADD_ACTIONS_WITH_STANDALONE_ATTRIBUTE}]`]: {
      gap: theme.spacing(0.5),
      "& .MuiButton-root": { paddingInline: theme.spacing(0.5) },
      "& .MuiButton-startIcon": { marginRight: theme.spacing(0.5) },
      "& .MuiButtonGroup-root > .MuiButton-root:last-of-type": { minWidth: 32, paddingInline: 0 },
    },
  },
}));

/**
 * The search box's place on that row. MRT wraps the field in a horizontal `Collapse`, and that wrapper
 * is the flex item — so the floor has to be set on it rather than on the field inside it.
 */
const CollectionToolbarSearchRoot = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flex: "0 1 auto",
  minWidth: 0,
  "& > .MuiCollapse-root": {
    flexShrink: 0,
  },
}));

export function CollectionToolbarSearch(props: Readonly<ComponentProps<typeof CollectionToolbarSearchRoot>>) {
  return <CollectionToolbarSearchRoot {...{ [COLLECTION_TOOLBAR_SEARCH_ATTRIBUTE]: "" }} {...props} />;
}

/**
 * The view buttons at the far end of that row — filter, columns, full screen.
 *
 * `margin-left: auto` rather than `justify-content: space-between` on the row: the row wraps, and
 * `space-between` would spread whatever ends up on the second line across its whole width instead of
 * keeping the add control beside the search it belongs to.
 */
export const CollectionToolbarViewButtons = styled(ToolbarButtons)(({ theme }) => ({
  marginLeft: "auto",
  /**
   * On a phone the last icon's glyph, not its button, meets the toolbar's right-hand edge — the edge
   * the templates expander above it ends on. An icon button pads its glyph by 8px, so the full screen
   * icon stood 8px short of every line around it (#5907).
   */
  [`@container ${TOOLBAR_CONTAINER} (width < ${TOOLBAR_IS_PHONE_SIZED})`]: {
    marginRight: theme.spacing(-1),
  },
}));

/**
 * Spacing and size for the search box where it sits inline with the buttons on a wide screen.
 *
 * The width is the point. A `TextField` left to itself is however wide MUI's default input is —
 * 189px of text — and every page got exactly that, so the box fitted "Search inbox" and cut
 * "Search teams working for me" off at "Search teams working fo". The pages that looked right were
 * the ones whose names happened to be short. 280px holds the longest name the app uses with room
 * to spare, and measured from `md` to 1920px every page has the room for it — contracts, the
 * tightest toolbar of them, only after its filter chips stopped competing with its tab strip.
 *
 * On a phone the box fills its row instead, with no margin, as `phoneSearchFillsTheRow` has it (#7224).
 */
export const searchFieldSpacing = { mr: { xs: 0, sm: 5 }, mt: 1, width: { xs: "100%", sm: 280 } } as const;

/**
 * The same 280px for a search box MRT draws in its own toolbar, through `muiSearchTextFieldProps`'
 * `sx`. Left at MUI's default the box cut "Search work to add them to" off at "Search work to add
 * th…" with half the toolbar empty beside it (#6721). It never grows past the toolbar it sits in.
 */
export const searchFieldWidth = { width: searchFieldSpacing.width, maxWidth: "100%" } as const;

/**
 * Keeps a top toolbar inside the page it is drawn on, for `muiTopToolbarProps`.
 *
 * MRT lays the top toolbar out as a grid, and the flex Box inside it keeps the default
 * `min-width: auto` — so the grid track sizes itself to that Box's *content*. A tab strip wider
 * than a phone therefore holds the whole toolbar open past the viewport, and the toolbar, being
 * `overflow-x: hidden`, clips whatever ends up past the right edge with no scrollbar to suggest
 * it is there. A zero floor lets the track clamp to the page instead.
 *
 * This is one third of the arrangement. The containers between the toolbar and the tabs need
 * `minWidth: 0` of their own, and the tabs themselves must be `variant="scrollable"` with a zero
 * floor on `.MuiTabs-scroller`; none of the three does anything without the others. See
 * [table toolbars](../../../../../docs/software-design/table-toolbars.md).
 */
export const topToolbarFitsThePage = {
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  "& > .MuiBox-root": {
    gap: 0,
    minWidth: 0,
    overflow: "hidden",
  },
} as const;

/**
 * The standard search box, for `muiSearchTextFieldProps`.
 *
 * `searches` names what is being searched, so the placeholder reads "Search archived templates"
 * rather than the bare "Search" a reader has to work out from context. Pass the table instance and
 * the clear button wires itself up; a page holding the filter in its own state can pass `onClear`
 * instead.
 */
export function searchTextFieldProps<TData extends MRT_RowData>(params: {
  searches: string;
  table: MRT_TableInstance<TData>;
  onClear?: () => void;
  maxLength?: number;
}) {
  const { maxLength, onClear, searches, table } = params;
  const clear = onClear ?? (() => table.setGlobalFilter(""));
  const hasQuery = Boolean(table.getState().globalFilter);
  return {
    placeholder: `Search ${searches}`,
    slotProps: {
      input: {
        // Where the name is longer than the box — a narrow row, a longer name than the ones here
        // today — say so rather than stopping mid-word. The rule has to sit on the input itself;
        // on `::placeholder` it does nothing. It lapses while the field is focused, so a long
        // query still scrolls with the caret the way it always did.
        sx: { "& input": { textOverflow: "ellipsis" } },
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: hasQuery ? (
          <InputAdornment position="end">
            <IconButton aria-label="Clear search" size="small" onClick={clear}>
              <CloseIcon />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      },
      htmlInput: maxLength === undefined ? undefined : { maxLength },
    },
  };
}

/**
 * Whether any column of the table can be filtered, so the Toggle filters button has something to
 * show. A table of display columns, or of columns whose server takes no filter, answers false: the
 * button would open an empty row (#5453).
 */
export function canFilterSomeColumn<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): boolean {
  return table.getAllLeafColumns().some((column) => column.getCanFilter());
}
